import React, { ReactNode, useEffect, useState } from 'react';
import NormalMessage from '../common/NormalMessage';
import { useSearchParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify'; // Import toast
import SharedData from '../common/SharedData';
import Paginator from '../common/Paginator';
import PaymentReportTable from './payment/PaymentReportTable';
import UploadInvoiceModal from './payment/UploadInvoiceModal';
import { IMonthlyPaymentItem } from './payment/PaymentReportTableRow'; // Import the exported interface
import SettlementSummaryCards from './payment/SettlementSummaryCards';
import { invoiceUploadVisible } from './payment/paymentActionsGate';
import { prepareFilters } from '../orders/core/_requests';
import { getMonthlyPayments, uploadInvoice } from './core/_requests'; // Import uploadInvoice
import { TutorialVideoLink } from '../../tenant/TutorialVideoLink';
import './payment-video.css'; // Import the CSS for the animation

type Props = {
    className?: string;
}

const PaymentReports: React.FC<Props> = ({ className }) => { 
    const { formatMessage } = useIntl()
    const [searchParams, setSearchParams] = useSearchParams()

    const { currentUser, language } = SharedData();
    const [dataList, setDataList] = useState<IMonthlyPaymentItem[]>([]);
    // The API pages its list (30 a page). With weekly payments 30 rows is only ~7 months, so an
    // older payment still waiting for its invoice has to stay reachable.
    const [total, setTotal] = useState(0);
    const [perPage, setPerPage] = useState(30);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedItemForUpload, setSelectedItemForUpload] = useState<IMonthlyPaymentItem | null>(null);
    const [isUploading, setIsUploading] = useState(false); // Add state for loading
    const [showVideoHighlight, setShowVideoHighlight] = useState(false); // State to control the highlight animation

    // Function to fetch data
    const getDate = async () => { 
        try {
            // The shared Paginator keeps its page in `current_page`; the API reads `page`.
            const params = new URLSearchParams(searchParams);
            const currentPage = params.get('current_page');
            if (currentPage) {
                params.set('page', currentPage);
                params.delete('current_page');
            }
            const res = await getMonthlyPayments(params.toString(), currentUser?.token, language || 'en');
     
            if (res && res.data && res.data.data && Array.isArray(res.data.data.data)) { // Check nested structure and ensure it's an array
                setDataList(res.data.data.data); 
                setTotal(Number(res.data.data.total) || 0);
                setPerPage(Number(res.data.data.per_page) || 30);
            } else {
                setDataList([]); // Set to empty array if data is not in expected format
                setTotal(0);
                console.error("API response data is not in the expected format:", res?.data?.data);
            }

        } catch (exp: any) {
            console.error("Error fetching payment reports:", exp);
            setDataList([]); // Set to empty array on error
            setTotal(0);
        }
    };

    // Check for the cookie on component mount
    useEffect(() => {
        // Check if the payment_video cookie exists
        const hasSeenVideo = document.cookie.split(';').some(item => item.trim().startsWith('payment_video='));
        // Only show the highlight if the cookie doesn't exist
        setShowVideoHighlight(!hasSeenVideo);
    }, []);

    useEffect(() => {
        prepareFilters(searchParams, setSearchParams);
        getDate(); // Call fetch data function
    }, [searchParams, currentUser, language]); // Added dependencies

    // --- Modal Handlers ---
    const handleOpenUploadModal = (item: IMonthlyPaymentItem) => {
        setSelectedItemForUpload(item);
        setShowUploadModal(true);
    };

    const handleCloseUploadModal = () => {
        setShowUploadModal(false);
        setSelectedItemForUpload(null);
    };

    // Update to accept the API message string and show it in a toast
    const handleUploadSuccess = (messageToShow?: string) => { 
        handleCloseUploadModal(); // Close the modal first
        getDate(); // Refresh the data list

        // Show toast with the API message if it exists
        if (messageToShow) {
            const trimmedMessage = messageToShow.trim(); // Trim whitespace
            // Determine toast type based on the specific success message
            const isSpecificSuccess = trimmedMessage === 'Invoice uploaded successfully.';
            if (isSpecificSuccess) {
                toast.success(trimmedMessage, { autoClose: 8000 });
            } else {
                // Show other API messages as info (or adjust type as needed)
                toast.info(messageToShow, { autoClose: 8000 }); 
            }
        }
        // Note: The generic error toast for exceptions is handled in the catch block of uploadInvoiceHandler
    };

    // Handler for the actual upload logic
    const uploadInvoiceHandler = async (file: File) => {
        if (!selectedItemForUpload) {
            console.error("No item selected for upload.");
            // Optionally show an error message to the user
            return;
        }

        setIsUploading(true);
        try {
            // Capture the response
            const res = await uploadInvoice( 
                selectedItemForUpload.supplier_payment_id,
                file,
                currentUser?.token, 
                language || 'en'
            );
            const apiMessage = res?.data?.message; 
            handleUploadSuccess(apiMessage); 

        } catch (error) {
            console.error("Error uploading invoice:", error);
            handleUploadSuccess();
            toast.error(formatMessage({ id: 'REPORTS.PAYMENT.UPLOAD.ERROR' })); 
        } finally {
            setIsUploading(false);
        }
    };
    // --- End Modal Handlers ---

    const renderResults = (): ReactNode => {
        if (dataList.length > 0) {
            return (
                <div className='card-body py-3'>
                    {/* Pass the handleOpenUploadModal function as a prop */}
                    <PaymentReportTable data={dataList} onOpenUploadModal={handleOpenUploadModal} /> 
                    {total > perPage && (
                        <div className='d-flex justify-content-end'>
                            <Paginator totalResult={total} perPage={perPage} />
                        </div>
                    )}
                </div>
            );
        }

        // Display message if dataList is empty (could be initial state, no data, or error)
        return <NormalMessage msg={formatMessage({ id: 'no_data' })} />;
    }

    return (
        <> {/* Use Fragment to wrap table and modal */}
           {/* Settlement summary header — Amazon-style stat cards at the top
               so suppliers see "where's my money?" answers without
               scrolling. Component fail-soft renders an info banner on
               its own errors and never blocks the table below. */}
           <SettlementSummaryCards />

           <div className={`card ${className}`}>
    <div className='card-header border-0 pt-5'>
        <h3 className='card-title d-flex justify-content-between align-items-center w-100'>
            <span className='card-label fw-bold fs-3 mb-1'>
                {formatMessage({ id: 'monthly_payment' })}
            </span>
            <div className="position-relative">
                <TutorialVideoLink
                    section="payment_reports"
                    fallback="https://www.youtube.com/watch?v=YACmCN-7O1M"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-primary d-flex align-items-center ${showVideoHighlight ? 'video-highlight' : ''}`}
                    onClick={() => {
                        // Set the cookie when the video link is clicked
                        document.cookie = "payment_video=seen";
                        setShowVideoHighlight(false);
                    }}
                >
                    <i className='fab fa-youtube mx-2 text-danger fs-2'></i>
                    <span>{formatMessage({ id: 'video_link' })}</span>
                </TutorialVideoLink>
            </div>
        </h3>
    </div>
    {renderResults()}
</div>


            {/* Render the modal conditionally. Nothing can open it when the tenant requires no
                supplier invoice (the row hides its button), but keeping the gate here means the
                upload path is unreachable rather than merely unclicked. */}
            {selectedItemForUpload && invoiceUploadVisible() && (
                <UploadInvoiceModal
                    show={showUploadModal}
                    handleClose={handleCloseUploadModal}
                    // Pass the correct props based on UploadInvoiceModalProps
                    paymentId={selectedItemForUpload.supplier_payment_id} 
                    handleUpload={uploadInvoiceHandler} 
                    isLoading={isUploading} 
                />
            )}
        </>
    );
};

export { PaymentReports };
